/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useTixlySession from 'effects/useTixlySession';
import useGADataLayer from 'effects/gaDataLayer';
import Modal from 'components/Modal/Modal';
import Login from 'components/Menu/Login/Login';
import './Authorize.scss';

/**
 * This is the Authorize component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Authorize({
  itixUrl,
  sku,
  name,
  login: loginConfig,
  purchase: purchaseConfig,
  price,
  basket,
  labels,
}) {
  const {
    client: { saved },
  } = useTixlySession(itixUrl);
  const { trackItemView, trackSelectItem } = useGADataLayer();
  const [login, setLogin] = useState(false);

  useEffect(() => {
    trackItemView({
      item_id: sku,
      item_name: name,
      item_category: 'digitalt program',
      item_list_id: 'digitale-program',
      item_list_name: 'Digitale program',
      price: price,
      quantity: 1,
    });
  }, []);

  async function onBasketClick() {
    await trackSelectItem({
      item_id: sku,
      item_name: name,
      item_category: 'digitalt program',
      item_list_id: 'digitale-program',
      item_list_name: 'Digitale program',
      price: price,
      quantity: 1,
    });

    location.href = basket;
  }

  function renderPrice() {
    return (
      <div className="price">
        {price}
        <small>kr</small>
      </div>
    );
  }

  function renderBasket() {
    return (
      <button className="ticket-button" onClick={onBasketClick}>
        <span>{labels.purchase}</span>
      </button>
    );
  }

  function renderLoginAction() {
    if (loginConfig.type === 'link') {
      return (
        <a className="login-link" href={loginConfig.loginLink}>
          {labels.login}
        </a>
      );
    }

    return (
      <button
        onClick={() => {
          setLogin(true);
        }}
      >
        {labels.login}
      </button>
    );
  }

  function renderContent() {
    if (saved && loginConfig.type !== 'link') {
      return (
        <>
          <h2 className="category">{purchaseConfig.heading}</h2>
          <h3 className="h2">{purchaseConfig.title}</h3>
          <p>{purchaseConfig.body}</p>
          {renderPrice()}
          {renderBasket()}
          <footer>{purchaseConfig.footer}</footer>
        </>
      );
    }

    return (
      <>
        <h2 className="category">{loginConfig.heading}</h2>
        <h3 className="h2">{loginConfig.title}</h3>
        <p>{loginConfig.body}</p>
        {renderPrice()}
        {renderBasket()}
        <footer>
          {loginConfig.footer}
          {renderLoginAction()}
        </footer>
      </>
    );
  }

  if (login) {
    return (
      <Login
        {...loginConfig}
        close
        onClose={() => {
          setLogin(false);
        }}
        onSuccess={() => {
          setLogin(false);
        }}
      />
    );
  }

  return <Modal className="authorize">{renderContent()}</Modal>;
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Authorize.propTypes = {
  itixUrl: PropTypes.string.isRequired,
  login: PropTypes.shape({
    type: PropTypes.string,
    loginLink: PropTypes.string,
    heading: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    footer: PropTypes.string,
  }).isRequired,
  purchase: PropTypes.shape({
    heading: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    footer: PropTypes.string,
  }).isRequired,
  sku: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  basket: PropTypes.string.isRequired,
  labels: PropTypes.shape({
    purchase: PropTypes.string.isRequired,
    login: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Authorize.defaultProps = {
  itixUrl: '',
  type: 'link',
  login: {},
  purchase: {},
  price: 0,
  basket: '',
  labels: {
    purchase: 'Purchase',
    login: 'Login',
  },
};

export default Authorize;
